import { createContext, useContext, useState, useEffect } from "react";
import { redirectToLogin } from "../utils/auth";

const UserContext = createContext(null);

const readCachedUser = () => {
  try {
    const cached = JSON.parse(localStorage.getItem("user"));
    if (cached) {
      return {
        username: cached.email || localStorage.getItem("username") || "",
        isPartner: cached.isPartner ?? null,
        isPMO: cached.isPMO ?? false,
        name: cached.name ?? "",
        team: cached.team ?? "",
        usertype: cached.usertype ?? "",
        role: cached.role ?? {},
        canApproveLoa: cached.canApproveLoa ?? false,
        loaded: true,
      };
    }
  } catch (e) {
    /* corrupt cache — fall through to fetch */
  }
  return {
    username: localStorage.getItem("username") || "",
    isPartner: null,
    isPMO: false,
    name: "",
    team: "",
    usertype: "",
    role: {},
    canApproveLoa: false,
    loaded: false,
  };
};

export const UserProvider = ({ children }) => {
  // Hydrate synchronously from the data stored at verification time.
  // When present, `loaded` is already true and no network call is made.
  const [user, setUser] = useState(readCachedUser);

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setUser((prev) => ({ ...prev, loaded: true }));
      return;
    }

    // Already hydrated from localStorage — nothing to fetch.
    if (user.loaded) return;

    // Fallback only for sessions created before user data was cached at login.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/API/V2/users/me`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((r) => {
        if (r.status === 401) {
          redirectToLogin();
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (!data) return;
        // Cache the full public user record (PUBLIC_USER_FIELDS + computed flags)
        // as returned by the API — no picking/dropping fields here.
        localStorage.setItem("user", JSON.stringify(data));
        localStorage.setItem("username", data.email || "");
        setUser({
          username: data.email || "",
          isPartner: data.isPartner ?? null,
          isPMO: data.isPMO ?? false,
          name: data.name ?? "",
          team: data.team ?? "",
          usertype: data.usertype ?? "",
          role: data.role ?? {},
          canApproveLoa: data.canApproveLoa ?? false,
          loaded: true,
        });
      })
      .catch(() => {
        setUser((prev) => ({ ...prev, loaded: true }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
